import { ReactNode } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';

type StatusChip = { label: string; tone?: 'pos' | 'warn' | 'info' | 'muted' };

type Props = {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Gradient for icon tile — default portal accent */
  iconClassName?: string;
  statusChips?: StatusChip[];
  ctaLabel: string;
  hideLabel?: string;
  reportOpen: boolean;
  onToggle: () => void;
  children?: ReactNode;
};

const toneClass: Record<NonNullable<StatusChip['tone']>, string> = {
  pos: 'text-[rgb(var(--pos))]',
  warn: 'text-[rgb(var(--warn))]',
  info: 'text-[rgb(var(--info))]',
  muted: 'text-glass-text-3',
};

/**
 * Compact AI copilot card from the redesign handoff:
 * icon tile + AI pill + status chips + sheen CTA + expandable report.
 */
export default function AICopilotStrip({
  title,
  description,
  icon,
  iconClassName,
  statusChips = [],
  ctaLabel,
  hideLabel = 'Hide report',
  reportOpen,
  onToggle,
  children,
}: Props) {
  return (
    <div className="space-y-3">
      <div className="glass-card relative overflow-hidden p-4">
        <div
          className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full opacity-40 animate-ai-pulse"
          style={{
            background:
              'radial-gradient(circle, rgb(var(--accent-rgb) / 0.45) 0%, transparent 70%)',
          }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <div
            className={`w-10 h-10 rounded-[13px] flex items-center justify-center text-white shrink-0 ${
              iconClassName || ''
            }`}
            style={
              iconClassName
                ? undefined
                : {
                    background:
                      'linear-gradient(135deg, rgb(var(--accent-rgb)), rgb(var(--accent2-rgb)))',
                  }
            }
          >
            {icon || <Sparkles className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[0.95rem] font-semibold text-glass-text tracking-tightest">{title}</h3>
              <span
                className="text-[9px] font-extrabold tracking-[0.1em] px-2 py-0.5 rounded-full text-white"
                style={{
                  background:
                    'linear-gradient(135deg, rgb(var(--accent-rgb)), rgb(var(--accent2-rgb)))',
                }}
              >
                AI
              </span>
            </div>
            {description ? <p className="text-xs text-glass-text-3 mt-1">{description}</p> : null}
            {statusChips.length ? (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {statusChips.map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-1.5 text-[0.7rem] font-medium px-2 py-1 rounded-full bg-glass-3 border border-glass text-glass-text-2"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        c.tone === 'pos'
                          ? 'bg-[rgb(var(--pos))]'
                          : c.tone === 'warn'
                            ? 'bg-[rgb(var(--warn))]'
                            : c.tone === 'info'
                              ? 'bg-[rgb(var(--info))]'
                              : 'bg-glass-text-3'
                      }`}
                    />
                    <span className={c.tone ? toneClass[c.tone] : ''}>{c.label}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onToggle}
            className="relative overflow-hidden btn-accent px-4 py-2.5 text-sm font-semibold shrink-0"
          >
            <span className="relative z-10 flex items-center gap-1.5">
              {reportOpen ? hideLabel : ctaLabel}
              <ChevronDown className={`w-4 h-4 transition-transform ${reportOpen ? 'rotate-180' : ''}`} />
            </span>
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-ai-sheen" />
          </button>
        </div>
      </div>
      {reportOpen && children ? (
        <div className="glass-card p-5 animate-float-in">{children}</div>
      ) : null}
    </div>
  );
}
